'use client'

import { useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { getApiUrl } from '@/lib/serverConfig'
import { routing, type AppLocale } from '@/i18n/routing'
import { normalizeRoute } from '@/lib/i18n/routeNormalize'
import { flatStringEntries } from '@/lib/i18n/flatMessages'
import { getDocsMessageCatalog } from '@/lib/i18n/docsRouteMessages'
import { getLearnMessageCatalog } from '@/lib/i18n/learnRouteMessages'
import {
  addI18nTemplateMatcher,
  findI18nTemplateMatches,
  toVisibleI18nText,
  type I18nTemplateMatcher,
} from '@/lib/i18n/templateMatchers'

const DEFAULT_BUNDLE_LOCALE: AppLocale = routing.defaultLocale as AppLocale

async function loadBundle(locale: AppLocale): Promise<Record<string, string>> {
  // Dynamic import so the messages JSON (~50KB per locale) is NOT pulled into the
  // global page bundle. It's only fetched when the bridge actually activates
  // inside the editor iframe. Switch is exhaustive over AppLocale so adding a new
  // locale fails at typecheck — silently falling back to `en` here means the
  // inverse map gets built from the wrong language and nothing on the page
  // matches, which looks identical to "the editor doesn't work."
  // Flat-hash view only — the iframe inverse map (text → hash) is built from
  // flat extract hashes; nested namespaces (e.g. `email.*`) have no iframe
  // click-to-edit and are dropped. See `flatStringEntries`.
  switch (locale) {
    case 'en':
      return {
        ...flatStringEntries((await import('../../../messages/en.json')).default),
        ...getDocsMessageCatalog(locale),
        ...getLearnMessageCatalog(locale),
      }
    case 'ja':
      return {
        ...flatStringEntries((await import('../../../messages/ja.json')).default),
        ...getDocsMessageCatalog(locale),
        ...getLearnMessageCatalog(locale),
      }
    case 'es':
      return {
        ...flatStringEntries((await import('../../../messages/es.json')).default),
        ...getDocsMessageCatalog(locale),
        ...getLearnMessageCatalog(locale),
      }
    case 'ar':
      return {
        ...flatStringEntries((await import('../../../messages/ar.json')).default),
        ...getDocsMessageCatalog(locale),
        ...getLearnMessageCatalog(locale),
      }
    case 'ko':
      return {
        ...flatStringEntries((await import('../../../messages/ko.json')).default),
        ...getDocsMessageCatalog(locale),
        ...getLearnMessageCatalog(locale),
      }
  }
}

const DATA_ATTR_HASH = 'data-i18n-hash'
const DATA_ATTR_OCCURRENCE = 'data-i18n-occurrence'
const DATA_ATTR_CANDIDATES = 'data-i18n-candidates'
// Set on every element matching the sidebar's currently-selected hash so CSS
// can keep the blue outline lit even when the cursor leaves it.
const DATA_ATTR_SELECTED = 'data-i18n-selected'
const DATA_ATTR_RICH = 'data-i18n-rich'
const DATA_ATTR_STRUCTURED_LIST = 'data-i18n-structured-list'
const RICH_TEXT_SELECTOR = 'p,li,h1,h2,h3,h4,h5,h6,button,a,label,td,th,address'
const RICH_TEXT_MAX_LENGTH = 2000
// Saved bundle/source text for an element whose visible copy has been replaced
// with a parent-posted override. Used to restore the original text when the
// override is removed (reset to bundle) without round-tripping through the
// server. Held on the parent element since the text node itself is mutated.
const DATA_ATTR_ORIGINAL = 'data-i18n-original'

const BULLET_ITEM_RE = /^-\s+(.+)$/

export function parseStructuredListOverride(value: string): string[] | null {
  const lines = value.trim().split('\n')
  if (lines.length === 0) return null
  const items = lines.map(line => line.match(BULLET_ITEM_RE)?.[1])
  return items.every(Boolean) ? (items as string[]) : null
}

export function applyStructuredListOverride(
  el: Element,
  override: string,
  originalChildren: WeakMap<Element, Node[]>,
): boolean {
  const items = parseStructuredListOverride(override)
  if (!items) return false

  const currentItems = Array.from(el.children).map(child => child.textContent?.trim() ?? '')
  if (currentItems.length === items.length && currentItems.every((item, index) => item === items[index])) return true

  if (!originalChildren.has(el)) {
    originalChildren.set(
      el,
      Array.from(el.childNodes).map(child => child.cloneNode(true)),
    )
  }
  if (!el.hasAttribute(DATA_ATTR_ORIGINAL)) el.setAttribute(DATA_ATTR_ORIGINAL, '')

  const template = Array.from(el.children).find(child => child.tagName === 'LI')
  const translatedItems = items.map(item => {
    const listItem = template?.cloneNode(false) ?? document.createElement('li')
    listItem.textContent = item
    return listItem
  })
  el.replaceChildren(...translatedItems)
  return true
}

export function restoreStructuredListOriginal(el: Element, originalChildren: WeakMap<Element, Node[]>): boolean {
  const children = originalChildren.get(el)
  if (!children) return false
  el.replaceChildren(...children.map(child => child.cloneNode(true)))
  originalChildren.delete(el)
  el.removeAttribute(DATA_ATTR_ORIGINAL)
  return true
}

// Trailing debounce window for the route-coverage post. The MutationObserver
// triggers a re-scan on every mutation; we wait until the page has been quiet
// for this long before snapshotting the hash set and posting it to the parent.
// Long enough to catch async-hydrated content, short enough that switching
// pages feels responsive.
const RENDERED_DEBOUNCE_MS = 1500

// Cap the number of hashes a single bridge post can emit. Matches the backend
// validator so we fail fast on the client rather than wasting a round trip.
const RENDERED_HASH_CAP = 2000

// Navigation guard for client-side redirects in the iframe is installed by an
// SSR-inlined script (see `TranslateNavigationGuardScript.tsx`), which runs
// during HTML parse before any client component chunk loads. The bridge's
// hot path stays focused on DOM annotation + postMessage; nothing here.

interface BridgeOptions {
  locale: AppLocale
}

/**
 * In-iframe bridge for the parallel-route translation editor.
 *
 * The bridge ONLY activates when ALL of:
 *   1. `window.parent !== window` (we're framed)
 *   2. `window.parent.origin === window.location.origin` (same-origin parent)
 *   3. `window.parent.location.pathname.startsWith('/translate/')` (parent is the editor)
 *
 * Activation flow:
 *   - Fetch the live override map from the backend, merge with the bundled
 *     locale JSON to get the "effective" messages, then build a `value → hash[]`
 *     inverse map. The merged source is what's actually rendered, so the map
 *     can find any string the user sees.
 *   - Walk the DOM with a `MutationObserver`; for each text node whose content
 *     matches an entry in the inverse map, tag its parent element with
 *     `data-i18n-hash` (+ `data-i18n-candidates` on collisions) so CSS can
 *     style hover affordances.
 *   - On click of any tagged element, postMessage `{ type: 'i18n:select', hash,
 *     occurrence, surrounding }` to the parent — exact-origin targetOrigin.
 *   - Listen for parent → iframe messages with origin + source validation:
 *     `i18n:revalidate` → `router.refresh()` (re-runs RSC → fetchOverrides) then
 *     rebuild the inverse map and re-scan.
 *   - `i18n:scrollTo` → scroll the matching node into view.
 *
 * CSS hover affordances are wired via `data-translate-mode='1'` on `<html>`.
 */
export function TranslateModeBridge({ locale }: BridgeOptions) {
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === 'undefined') return

    let enabled = false
    try {
      if (window.parent === window) return
      if (window.parent.origin !== window.location.origin) return
      if (!window.parent.location.pathname.startsWith('/translate/')) return
      enabled = true
    } catch {
      return // cross-origin access throws; bail.
    }
    if (!enabled) return

    // Project editor/canvas routes are highly dynamic and can mutate frequently.
    // Keep click-to-translate active there, but avoid the live whole-document
    // MutationObserver that can thrash while the editor/canvas is updating.
    const useLimitedAnnotation = /^\/p\/[^/]+/.test(window.location.pathname)

    let bundleMessages: Record<string, string> = {}
    // Default-locale bundle keyed off the same hashes. When the locale bundle is
    // missing entries (e.g. `messages/ko.json` is `{}` while we're staging
    // Korean), request.ts's `mergeWithSourceFallback` fills them with the EN
    // value at render time. The bridge has to include those same fallbacks in
    // the inverse map or rendered fallback text never gets `data-i18n-hash` and
    // the editor can't click it.
    let defaultBundleMessages: Record<string, string> = {}
    let inverseMap = new Map<string, string[]>()
    let templateMatchers: I18nTemplateMatcher[] = []
    let observer: MutationObserver | null = null
    let cancelled = false
    // Overrides pushed from the parent sidebar via `i18n:overrides` postMessage.
    // The public `/i18n/overrides/:locale` endpoint filters by `enabled`, so for
    // disabled locales the iframe's own fetches AND request.ts's RSC fetch both
    // return empty maps. The sidebar reads from the editor-authenticated admin
    // endpoint and pushes the resulting hash → value map into the iframe so we
    // can preview translations live without enabling the locale publicly.
    let parentOverrides: Record<string, string> = {}
    let selectedHash: string | null = null
    const originalStructuredChildren = new WeakMap<Element, Node[]>()

    // Route-coverage state — scoped to this useEffect run so the fingerprint
    // cache resets when the pathname changes (we WANT to re-post on a fresh
    // route). Within a single pathname, the cache suppresses redundant posts.
    const lastFingerprintByRoute = new Map<string, string>()
    let renderedPostTimer: ReturnType<typeof setTimeout> | null = null
    const limitedScanTimers: ReturnType<typeof setTimeout>[] = []

    /**
     * Returns the rendered hash set in DOM order (with duplicates removed).
     * The sidebar's "Strings on this page" list renders rows in this order so
     * the list visually matches the page when translators scan top-to-bottom.
     */
    function collectRenderedHashes(): string[] {
      const ordered: string[] = []
      const seen = new Set<string>()
      function add(h: string | null) {
        if (!h) return
        if (seen.has(h)) return
        seen.add(h)
        ordered.push(h)
      }
      document.querySelectorAll(`[${DATA_ATTR_HASH}]`).forEach(el => {
        add(el.getAttribute(DATA_ATTR_HASH))
        const candidatesAttr = el.getAttribute(DATA_ATTR_CANDIDATES)
        if (candidatesAttr) {
          for (const h of candidatesAttr.split('|')) add(h)
        }
      })
      return ordered
    }

    function postRenderedSnapshot() {
      renderedPostTimer = null
      const route = normalizeRoute(window.location.pathname, routing.locales)
      const hashes = collectRenderedHashes()
      if (hashes.length > RENDERED_HASH_CAP) return
      // Fingerprint sorts so it's order-independent — we only re-post when the
      // set itself changes, not when the same set reflows.
      const fingerprint = [...hashes].sort().join('|')
      if (lastFingerprintByRoute.get(route) === fingerprint) return
      lastFingerprintByRoute.set(route, fingerprint)
      try {
        // Posted for every route — including dynamic ones like /p/:id — so the
        // sidebar's "Strings on this page" stays populated when a translator
        // opens the editor. The parent listener decides whether to persist the
        // payload (`/i18n/route-keys` rejects dynamic routes, so the bridge
        // doesn't filter — the parent does).
        window.parent.postMessage({ type: 'i18n:rendered', route, hashes }, window.location.origin)
      } catch {
        // ignore — parent may have navigated.
      }
    }

    function scheduleRenderedPost() {
      if (renderedPostTimer !== null) clearTimeout(renderedPostTimer)
      renderedPostTimer = setTimeout(postRenderedSnapshot, RENDERED_DEBOUNCE_MS)
    }

    function rebuildInverseMap(extra: Record<string, string>) {
      // For each hash, register EVERY known value (default bundle, locale
      // bundle, fetched override, parent override) — not just the "winning"
      // top-level merge. Without this, applying a parent override whose value
      // happens to collide with another hash's bundle value would leave the
      // bundle text un-annotated (its only mapping in a flat merge has been
      // clobbered), and the annotator couldn't find the element to swap.
      const allHashes = new Set<string>([
        ...Object.keys(defaultBundleMessages),
        ...Object.keys(bundleMessages),
        ...Object.keys(extra),
        ...Object.keys(parentOverrides),
      ])
      const next = new Map<string, string[]>()
      const nextTemplateMatchers: I18nTemplateMatcher[] = []
      const seenTemplateMatchers = new Set<string>()
      function register(value: string | undefined, hash: string) {
        if (!value) return
        addI18nTemplateMatcher(nextTemplateMatchers, seenTemplateMatchers, value, hash)
        const visibleValue = toVisibleI18nText(value)
        if (visibleValue && visibleValue !== value) {
          addI18nTemplateMatcher(nextTemplateMatchers, seenTemplateMatchers, visibleValue, hash)
          const visibleExisting = next.get(visibleValue)
          if (!visibleExisting) {
            next.set(visibleValue, [hash])
          } else if (!visibleExisting.includes(hash)) {
            visibleExisting.push(hash)
          }
        }
        const existing = next.get(value)
        if (!existing) {
          next.set(value, [hash])
        } else if (!existing.includes(hash)) {
          existing.push(hash)
        }
      }
      for (const hash of allHashes) {
        register(defaultBundleMessages[hash], hash)
        register(bundleMessages[hash], hash)
        register(extra[hash], hash)
        register(parentOverrides[hash], hash)
      }
      inverseMap = next
      templateMatchers = nextTemplateMatchers
    }

    function matchesForRenderedValue(value: string): string[] | undefined {
      return inverseMap.get(value) ?? findI18nTemplateMatches(value, templateMatchers)
    }

    async function fetchAndRebuild() {
      try {
        const res = await fetch(`${getApiUrl()}/i18n/overrides/${locale}`, { cache: 'no-store' })
        if (!res.ok) {
          rebuildInverseMap({})
          return
        }
        const data = (await res.json()) as { overrides?: Record<string, string> }
        rebuildInverseMap(data.overrides ?? {})
      } catch {
        rebuildInverseMap({})
      }
    }

    /**
     * Replace the visible text of an annotated element with `override`, saving
     * the original text on `data-i18n-original` the first time. Whitespace
     * around the trimmed text is preserved so layout-sensitive copy doesn't
     * shift. No-op if the element has no text node or the override already
     * matches the current text.
     */
    function applyOverrideToElement(el: Element, override: string) {
      if (
        el.hasAttribute(DATA_ATTR_STRUCTURED_LIST) &&
        applyStructuredListOverride(el, override, originalStructuredChildren)
      ) {
        return
      }
      const trimmedOverride = override.trim()
      const placeholderControl = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ? el : null
      if (placeholderControl && placeholderControl.placeholder.trim()) {
        if (placeholderControl.placeholder.trim() === trimmedOverride) return
        if (!el.hasAttribute(DATA_ATTR_ORIGINAL)) {
          el.setAttribute(DATA_ATTR_ORIGINAL, placeholderControl.placeholder)
        }
        placeholderControl.placeholder = trimmedOverride
        return
      }
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType !== Node.TEXT_NODE) continue
        const text = child as Text
        const current = text.nodeValue ?? ''
        if (current.trim() === trimmedOverride) return
        if (!el.hasAttribute(DATA_ATTR_ORIGINAL)) {
          el.setAttribute(DATA_ATTR_ORIGINAL, current)
        }
        const leading = current.match(/^\s*/)?.[0] ?? ''
        const trailing = current.match(/\s*$/)?.[0] ?? ''
        text.nodeValue = leading + trimmedOverride + trailing
        return
      }
    }

    function restoreOriginalForElement(el: Element) {
      if (el.hasAttribute(DATA_ATTR_STRUCTURED_LIST) && restoreStructuredListOriginal(el, originalStructuredChildren)) {
        return
      }
      const original = el.getAttribute(DATA_ATTR_ORIGINAL)
      if (original === null) return
      const placeholderControl = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement ? el : null
      if (placeholderControl) {
        if (placeholderControl.placeholder !== original) placeholderControl.placeholder = original
        el.removeAttribute(DATA_ATTR_ORIGINAL)
        return
      }
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType !== Node.TEXT_NODE) continue
        const text = child as Text
        if (text.nodeValue !== original) text.nodeValue = original
        el.removeAttribute(DATA_ATTR_ORIGINAL)
        return
      }
      // No text node remained (element re-rendered) — drop the stale marker so
      // a future override doesn't accidentally restore obsolete copy.
      el.removeAttribute(DATA_ATTR_ORIGINAL)
    }

    function reconcileOverrideForElement(el: Element) {
      const hash = el.getAttribute(DATA_ATTR_HASH)
      if (!hash) return
      const override = parentOverrides[hash]
      if (override !== undefined && override !== '') {
        applyOverrideToElement(el, override)
      } else if (el.hasAttribute(DATA_ATTR_ORIGINAL)) {
        restoreOriginalForElement(el)
      }
    }

    function applySelectionToElement(el: Element) {
      const isSelected = !!selectedHash && el.getAttribute(DATA_ATTR_HASH) === selectedHash
      if (isSelected) {
        if (!el.hasAttribute(DATA_ATTR_SELECTED)) el.setAttribute(DATA_ATTR_SELECTED, '')
      } else if (el.hasAttribute(DATA_ATTR_SELECTED)) {
        el.removeAttribute(DATA_ATTR_SELECTED)
      }
    }

    function reapplySelection() {
      // Clear any element that no longer matches AND tag every element that
      // does. Selection may be null (nothing highlighted) or apply to multiple
      // elements when the same hash appears more than once on the page.
      document.querySelectorAll(`[${DATA_ATTR_SELECTED}]`).forEach(el => {
        if (!selectedHash || el.getAttribute(DATA_ATTR_HASH) !== selectedHash) {
          el.removeAttribute(DATA_ATTR_SELECTED)
        }
      })
      if (!selectedHash) return
      document
        .querySelectorAll(`[${DATA_ATTR_HASH}="${CSS.escape(selectedHash)}"]`)
        .forEach(el => el.setAttribute(DATA_ATTR_SELECTED, ''))
    }

    function reconcileAllOverrides() {
      // Restore anything no longer overridden BEFORE annotating, so reset-to-bundle
      // produces the bundle text the annotator will then re-map.
      document.querySelectorAll(`[${DATA_ATTR_ORIGINAL}]`).forEach(el => {
        const hash = el.getAttribute(DATA_ATTR_HASH)
        if (!hash || parentOverrides[hash] === undefined || parentOverrides[hash] === '') {
          restoreOriginalForElement(el)
        }
      })
      document.querySelectorAll(`[${DATA_ATTR_HASH}]`).forEach(el => {
        if (!el.hasAttribute(DATA_ATTR_RICH) || el.hasAttribute(DATA_ATTR_STRUCTURED_LIST)) {
          reconcileOverrideForElement(el)
        }
      })
    }

    async function loadBundleAndPrime() {
      const [localeBundle, defaultBundle] = await Promise.all([
        loadBundle(locale),
        locale === DEFAULT_BUNDLE_LOCALE ? Promise.resolve({}) : loadBundle(DEFAULT_BUNDLE_LOCALE),
      ])
      bundleMessages = localeBundle
      defaultBundleMessages = defaultBundle
      await fetchAndRebuild()
    }

    function placeholderControlForElement(el: Element): HTMLInputElement | HTMLTextAreaElement | null {
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return el
      return null
    }

    function annotateElementMatch(el: Element, matches: string[] | undefined) {
      if (!matches || matches.length === 0) {
        if (el.hasAttribute(DATA_ATTR_HASH)) el.removeAttribute(DATA_ATTR_HASH)
        if (el.hasAttribute(DATA_ATTR_CANDIDATES)) el.removeAttribute(DATA_ATTR_CANDIDATES)
        return
      }
      const currentHash = el.getAttribute(DATA_ATTR_HASH)
      const hash = currentHash && matches.includes(currentHash) ? currentHash : matches[0]
      if (el.getAttribute(DATA_ATTR_HASH) !== hash) el.setAttribute(DATA_ATTR_HASH, hash)
      if (matches.length > 1) {
        const candidates = matches.join('|')
        if (el.getAttribute(DATA_ATTR_CANDIDATES) !== candidates) el.setAttribute(DATA_ATTR_CANDIDATES, candidates)
      } else if (el.hasAttribute(DATA_ATTR_CANDIDATES)) {
        el.removeAttribute(DATA_ATTR_CANDIDATES)
      }
      if (!el.hasAttribute(DATA_ATTR_RICH) || el.hasAttribute(DATA_ATTR_STRUCTURED_LIST)) {
        reconcileOverrideForElement(el)
      }
      applySelectionToElement(el)
    }

    function annotatePlaceholderElement(el: Element) {
      const control = placeholderControlForElement(el)
      if (!control) return
      const value = control.placeholder.trim()
      annotateElementMatch(control, value ? matchesForRenderedValue(value) : undefined)
    }

    function hasElementChildren(el: Element): boolean {
      for (const child of Array.from(el.childNodes)) {
        if (child.nodeType === Node.ELEMENT_NODE) return true
      }
      return false
    }

    function annotateRichElement(el: Element) {
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return
      if (!el.matches(RICH_TEXT_SELECTOR)) return
      if (!hasElementChildren(el)) return
      const value = el.textContent?.trim()
      if (!value || value.length > RICH_TEXT_MAX_LENGTH) return
      const matches = value ? matchesForRenderedValue(value) : undefined
      if (!matches || matches.length === 0) {
        if (el.hasAttribute(DATA_ATTR_RICH)) {
          el.removeAttribute(DATA_ATTR_RICH)
          annotateElementMatch(el, undefined)
        }
        return
      }
      if (!el.hasAttribute(DATA_ATTR_RICH)) el.setAttribute(DATA_ATTR_RICH, '')
      annotateElementMatch(el, matches)
    }

    function annotateTextNode(node: Text) {
      const parent = node.parentElement
      if (!parent) return
      if (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE') return
      // A parent already classified as rich is a single composite unit keyed off
      // its full textContent. Always re-drive it through annotateRichElement and
      // never annotate it from an individual child text node — a fragment could
      // match a *different* key (e.g. a "Privacy Policy" link inside a sentence)
      // and clobber the composite hash/override until the next full scan. This
      // also makes the characterData-mutation path self-correcting.
      if (parent.hasAttribute(DATA_ATTR_RICH)) {
        annotateRichElement(parent)
        return
      }
      const value = node.nodeValue?.trim()
      const matches = value ? matchesForRenderedValue(value) : undefined
      annotateElementMatch(parent, matches)
    }

    function scan(root: Node = document.body) {
      if (root instanceof Element) {
        annotatePlaceholderElement(root)
        root.querySelectorAll('input[placeholder], textarea[placeholder]').forEach(annotatePlaceholderElement)
        // Tag rich-text containers BEFORE walking text nodes so the text-node
        // walk below already sees the data-i18n-rich marker and defers to the
        // composite annotation, rather than briefly annotating the parent from a
        // matching child fragment (which can also trip override reconciliation).
        annotateRichElement(root)
        root.querySelectorAll(RICH_TEXT_SELECTOR).forEach(annotateRichElement)
      }
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode() as Text | null
      while (node) {
        annotateTextNode(node)
        node = walker.nextNode() as Text | null
      }
    }

    function selectAnnotated(event: MouseEvent) {
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      let tagged = target.closest<HTMLElement>(`[${DATA_ATTR_HASH}]`)
      if (!tagged && useLimitedAnnotation) {
        scan(target)
        scheduleRenderedPost()
        tagged = target.closest<HTMLElement>(`[${DATA_ATTR_HASH}]`)
      }
      if (!tagged) return
      const hash = tagged.getAttribute(DATA_ATTR_HASH)
      if (!hash) return
      // Capture rules:
      //   - Form controls are only captured for empty input/textarea placeholders;
      //     filled controls and selects/labels still focus/operate normally.
      //   - Non-left clicks are never captured (let middle/right pass through).
      //   - Any modifier (⌘/Ctrl/Shift/Alt) bypasses string selection. The
      //     beforeInteractive nav guard separately captures same-origin anchor
      //     clicks that would open a new tab/window and reroutes them through
      //     the translator iframe shell.
      //   - Otherwise: plain left-click edits the string, even on a link/button label.
      if (event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const formControl = target.closest('input, textarea, select, label')
      if (formControl) {
        const placeholderControl = placeholderControlForElement(tagged)
        if (!placeholderControl || placeholderControl.value !== '' || tagged !== formControl) return
      }
      event.preventDefault()
      event.stopPropagation()
      const placeholderControl = placeholderControlForElement(tagged)
      const surrounding = placeholderControl?.placeholder.slice(0, 200) ?? tagged.textContent?.slice(0, 200) ?? ''
      const occurrence = Number(tagged.getAttribute(DATA_ATTR_OCCURRENCE) ?? '0')
      try {
        window.parent.postMessage({ type: 'i18n:select', hash, occurrence, surrounding }, window.location.origin)
      } catch {
        // ignore — parent may have navigated.
      }
    }

    function scanInteractionTarget(event: MouseEvent) {
      if (!useLimitedAnnotation) return
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      if (target.closest(`[${DATA_ATTR_HASH}]`)) return
      scan(target)
      scheduleRenderedPost()
    }

    function handleParentMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (event.source !== window.parent) return
      const data = event.data
      if (!data || typeof data !== 'object') return
      const type = (data as { type?: string }).type
      if (type === 'i18n:revalidate') {
        // Re-fetch overrides + force RSC re-execution. router.refresh() makes
        // request.ts re-run with the latest tag state. router.refresh() returns
        // synchronously but the DOM update is async, so the MutationObserver picks
        // up changed text nodes; we still rescan immediately to clear stale
        // annotations on elements that aren't going to change at all (e.g. an
        // override deletion that reverts to identical bundle copy).
        void fetchAndRebuild().then(() => {
          router.refresh()
          scan()
        })
      } else if (type === 'i18n:scrollTo') {
        const hash = (data as { hash?: unknown }).hash
        if (typeof hash !== 'string') return
        const el = document.querySelector(`[${DATA_ATTR_HASH}="${CSS.escape(hash)}"]`)
        if (el instanceof HTMLElement) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      } else if (type === 'i18n:selection') {
        const hash = (data as { hash?: unknown }).hash
        selectedHash = typeof hash === 'string' && hash.length > 0 ? hash : null
        reapplySelection()
      } else if (type === 'i18n:overrides') {
        // Authoritative editor view of overrides from the parent sidebar.
        // The parent fetches via the editor-authenticated admin endpoint, so
        // this is the only path that sees staged copy for disabled locales.
        const hashes = (data as { hashes?: unknown }).hashes
        if (!hashes || typeof hashes !== 'object' || Array.isArray(hashes)) return
        const next: Record<string, string> = {}
        for (const [k, v] of Object.entries(hashes as Record<string, unknown>)) {
          if (typeof v === 'string' && v !== '') next[k] = v
        }
        parentOverrides = next
        rebuildInverseMap({})
        reconcileAllOverrides()
      }
    }

    document.documentElement.dataset.translateMode = '1'

    // Prime the locale bundle + override map, retrying on transient failure. The
    // dynamic `import()` of the messages JSON can reject on a cold Next dev
    // compile (chunk load error); without a retry the bridge would set
    // `data-translate-mode` but never run `scan()` or attach the observer below,
    // leaving the page un-annotated (no hover/click affordance) until a manual
    // re-navigation remounts the bridge against the now-warm chunk. Retrying with
    // backoff lets it self-heal in place.
    const PRIME_MAX_ATTEMPTS = 5
    async function primeWithRetry(): Promise<boolean> {
      let lastError: unknown
      for (let attempt = 0; attempt < PRIME_MAX_ATTEMPTS; attempt++) {
        if (cancelled) return false
        try {
          await loadBundleAndPrime()
          return true
        } catch (err) {
          lastError = err
          // No retry left on the final attempt — don't sleep before giving up.
          if (attempt < PRIME_MAX_ATTEMPTS - 1) {
            await new Promise(resolve => setTimeout(resolve, 250 * (attempt + 1)))
          }
        }
      }
      // Surface a permanent failure so a dead (un-annotated) preview is
      // diagnosable instead of failing silently.
      console.warn('[translate] bridge bundle prime failed after retries', lastError)
      return false
    }

    void primeWithRetry().then(primed => {
      if (cancelled || !primed) return
      scan()
      scheduleRenderedPost()
      // Tell the parent we're alive so the sidebar can push its current
      // override map. The parent has no other reliable signal for iframe
      // re-mounts (router.refresh keeps the iframe element but reloads the
      // document), and we'd otherwise lose previewed overrides on navigation.
      try {
        window.parent.postMessage({ type: 'i18n:bridge-ready' }, window.location.origin)
      } catch {
        // ignore — parent may have navigated.
      }
      if (useLimitedAnnotation) {
        for (const delay of [300, 1000, 2500, 5000]) {
          limitedScanTimers.push(
            setTimeout(() => {
              if (cancelled) return
              scan()
              scheduleRenderedPost()
            }, delay),
          )
        }
      } else {
        observer = new MutationObserver(records => {
          for (const record of records) {
            if (record.type === 'characterData' && record.target.nodeType === Node.TEXT_NODE) {
              annotateTextNode(record.target as Text)
            } else if (record.type === 'childList') {
              for (const added of Array.from(record.addedNodes)) {
                if (added.nodeType === Node.TEXT_NODE) annotateTextNode(added as Text)
                else if (added.nodeType === Node.ELEMENT_NODE) scan(added)
              }
              if (record.target instanceof Element) annotateRichElement(record.target)
            }
          }
          // Any DOM mutation might have changed the rendered hash set; defer the
          // snapshot to the trailing edge of the debounce window. Fingerprint
          // check inside postRenderedSnapshot suppresses redundant posts.
          scheduleRenderedPost()
        })
        observer.observe(document.body, { subtree: true, childList: true, characterData: true })
      }
    })

    // Mirror the modifier-bypass string-selection rule into CSS so the I-beam
    // cursor and sky-blue hover highlight disappear while a modifier is held.
    // Uses the same key set the click handler checks; window blur covers the
    // case where focus leaves the iframe while a key is still down.
    function syncModifier(event: KeyboardEvent | MouseEvent) {
      const held = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
      if (held) document.documentElement.dataset.translateModifier = '1'
      else delete document.documentElement.dataset.translateModifier
    }
    function clearModifier() {
      delete document.documentElement.dataset.translateModifier
    }

    document.addEventListener('pointerdown', selectAnnotated, true)
    document.addEventListener('click', selectAnnotated, true)
    document.addEventListener('mouseover', scanInteractionTarget, true)
    window.addEventListener('message', handleParentMessage)
    window.addEventListener('keydown', syncModifier)
    window.addEventListener('keyup', syncModifier)
    document.addEventListener('mousemove', syncModifier)
    window.addEventListener('blur', clearModifier)

    return () => {
      cancelled = true
      observer?.disconnect()
      for (const timer of limitedScanTimers) clearTimeout(timer)
      if (renderedPostTimer !== null) {
        clearTimeout(renderedPostTimer)
        renderedPostTimer = null
      }
      document.removeEventListener('pointerdown', selectAnnotated, true)
      document.removeEventListener('click', selectAnnotated, true)
      document.removeEventListener('mouseover', scanInteractionTarget, true)
      window.removeEventListener('message', handleParentMessage)
      window.removeEventListener('keydown', syncModifier)
      window.removeEventListener('keyup', syncModifier)
      document.removeEventListener('mousemove', syncModifier)
      window.removeEventListener('blur', clearModifier)
      delete document.documentElement.dataset.translateModifier
      delete document.documentElement.dataset.translateMode
      // Navigation guard intentionally persists for the iframe document's
      // lifetime — installed once at module load, never uninstalled. Iframe
      // navigations to a new pathname reload the document and re-run module
      // init, so each fresh page gets its own guard.
    }
  }, [locale, router, pathname])

  return null
}
